import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Paper,
  Button,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  LinearProgress,
  Box,
  Avatar,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import {
  Database as DatabaseIcon,
  CheckCircle2 as CheckCircleIcon,
  LayoutGrid as ChunksIcon,
  RefreshCw as RefreshIcon,
  Plus as AddIcon,
  UploadCloud as UploadIcon,
  Trash2 as DeleteIcon,
  Eye as VisibilityIcon,
  EyeOff as VisibilityOffIcon,
  FileText as DocIcon,
  Type as TextIcon,
  Link as LinkIcon,
} from "lucide-react";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { toast } from "react-toastify";
import { useTheme } from "@material-ui/core/styles";
import ChatiaDialog from "../../../components/ChatiaDialog";
import { CancelButton } from "../../../components/ActionButtons";
import { i18n } from "../../../translate/i18n";

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const StatCard = ({ title, value, icon, color, classes, isDark }) => (
  <Paper className={classes.statCard} variant="outlined" elevation={0}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <Typography className={classes.statLabel}>{title}</Typography>
        <Typography className={classes.statNumber}>{value}</Typography>
      </div>
      <div 
        className={classes.statIcon}
        style={{ 
          background: `linear-gradient(135deg, ${color} 0%, ${hexToRgba(color, 0.7)} 100%)`,
          boxShadow: `0 6px 16px ${hexToRgba(color, 0.35)}`,
        }}
      >
        {React.cloneElement(icon, { size: 20 })}
      </div>
    </div>
    <div 
      style={{ 
        position: "absolute", 
        inset: 0, 
        background: `linear-gradient(135deg, ${hexToRgba(color, 0.05)} 0%, transparent 55%)`,
        pointerEvents: "none",
      }} 
    />
  </Paper>
);

const statusMap = {
  pending: { label: "Pendente", color: "default" },
  processing: { label: "Processando", color: "primary" },
  indexed: { label: "Indexado", color: "primary" },
  error: { label: "Erro", color: "secondary" },
  active: { label: "Ativo", color: "primary" },
  importing: { label: "Importando", color: "primary" },
};

const sourceTypeOptions = [
  { value: "text", label: "Texto", icon: <TextIcon size={16} style={{ marginRight: 8 }} /> },
  { value: "pdf", label: "PDF", icon: <DocIcon size={16} style={{ marginRight: 8 }} /> },
  { value: "csv", label: "CSV", icon: <DocIcon size={16} style={{ marginRight: 8 }} /> },
  { value: "url", label: "URL", icon: <LinkIcon size={16} style={{ marginRight: 8 }} /> },
];

const RAGTab = ({ classes }) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const PRIMARY_MAIN = theme.palette.primary.main || "#1976d2";
  const t = (key, fallback, options) => {
    const fullKey = `knowledgeBase.rag.${key}`;
    const value = i18n.t(fullKey, options);
    return !value || value === fullKey ? fallback : value;
  };

  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [ingestOpen, setIngestOpen] = useState(false);
  const [selectedKB, setSelectedKB] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newSourceType, setNewSourceType] = useState("text");
  const [newChunkSize, setNewChunkSize] = useState(512);
  const [newChunkOverlap, setNewChunkOverlap] = useState(50);
  const [ingestContent, setIngestContent] = useState("");
  const [ingestFile, setIngestFile] = useState(null);
  const [ingestUrl, setIngestUrl] = useState("");
  const [ingestApiKey, setIngestApiKey] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [ingestMode, setIngestMode] = useState("file");

  const loadBases = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/knowledge-bases");
      setBases(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadBases(); }, [loadBases]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/knowledge-bases", {
        name: newName, description: newDescription,
        sourceType: newSourceType, chunkSize: newChunkSize, chunkOverlap: newChunkOverlap,
      });
      toast.success(t("toasts.created", "Base criada com sucesso!"));
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      loadBases();
    } catch (err) { toastError(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteBase", "Tem certeza que deseja excluir esta base de conhecimento?"))) return;
    try {
      await api.delete(`/knowledge-bases/${id}`);
      toast.success(t("toasts.deleted", "Base excluída!"));
      loadBases();
    } catch (err) { toastError(err); }
  };

  const handleIngest = async () => {
    if (!selectedKB || !ingestApiKey) return;
    setIngesting(true);
    try {
      if (ingestMode === "url" && ingestUrl.trim()) {
        await api.post("/knowledge-bases/ingest-url", { knowledgeBaseId: selectedKB.id, url: ingestUrl.trim(), apiKey: ingestApiKey }, { timeout: 300000 });
      } else if (ingestMode === "file" && ingestFile) {
        const formData = new FormData();
        formData.append("file", ingestFile);
        formData.append("knowledgeBaseId", String(selectedKB.id));
        formData.append("apiKey", ingestApiKey);
        await api.post("/knowledge-bases/ingest-file", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      } else if (ingestMode === "text" && ingestContent.trim()) {
        await api.post("/knowledge-bases/ingest", { knowledgeBaseId: selectedKB.id, content: ingestContent, apiKey: ingestApiKey }, { timeout: 300000 });
      } else {
        toast.error(t("toasts.fillContent", "Preencha o conteúdo, selecione um arquivo ou insira uma URL."));
        setIngesting(false);
        return;
      }
      toast.success(t("toasts.indexed", "Conteúdo indexado com sucesso!"));
      setIngestOpen(false);
      setIngestContent("");
      setIngestFile(null);
      setIngestUrl("");
      loadBases();
    } catch (err) { toastError(err); }
    setIngesting(false);
  };

  const totalChunks = bases.reduce((acc, b) => acc + (b.chunkCount || 0), 0);
  const indexedBases = bases.filter(b => b.status === "indexed").length;

  const menuProps = {
    PaperProps: {
      className: classes.reportSelectMenu,
    },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <DatabaseIcon size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {t("title", "Base de Conhecimento")}
              <span className={classes.countChip}>{bases.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {t("subtitle", "Fontes e conteúdos usados pelo agente para responder com RAG")}
            </div>
          </div>
        </div>

        <Box className={classes.headerActions}>
          <Button
            variant="outlined"
            size="small"
            onClick={loadBases}
            startIcon={<RefreshIcon size={16} />}
            style={{
              borderColor: PRIMARY_MAIN,
              color: PRIMARY_MAIN,
              borderRadius: 10,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("actions.refresh", "Atualizar")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setCreateOpen(true)}
            startIcon={<AddIcon size={16} />}
            style={{ borderRadius: 10, textTransform: "none", fontWeight: 600 }}
          >
            {t("actions.newBase", "Nova Base RAG")}
          </Button>
        </Box>
      </div>

      <div className={classes.statsBox}>
        <StatCard title={t("stats.created", "Bases criadas")} value={bases.length} icon={<DatabaseIcon />} color={PRIMARY_MAIN} classes={classes} isDark={isDark} />
        <StatCard title={t("stats.indexed", "Bases indexadas")} value={indexedBases} icon={<CheckCircleIcon />} color="#2EA85A" classes={classes} isDark={isDark} />
        <StatCard title={t("stats.totalChunks", "Chunks totais")} value={totalChunks.toLocaleString()} icon={<ChunksIcon />} color="#f59e0b" classes={classes} isDark={isDark} />
      </div>

      {loading && <LinearProgress style={{ margin: "0 16px 16px" }} />}

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tableScroll}>
          {bases.length === 0 && !loading ? (
            <div className={classes.emptyState}>
              <DocIcon size={64} className={classes.emptyIcon} style={{ opacity: 0.3, marginBottom: 16 }} />
              <Typography variant="h6" gutterBottom>{t("empty.title", "Nenhuma base de conhecimento criada")}</Typography>
              <Typography variant="body2" gutterBottom>
                {t("empty.description", "Crie uma base e faça upload de documentos (PDF, TXT, CSV) ou cole texto. O agente de IA usará essas informações para responder seus clientes.")}
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => setCreateOpen(true)} 
                startIcon={<AddIcon size={16} />} 
                style={{ marginTop: 16, borderRadius: 10 }}
              >
                {t("actions.createFirst", "Criar primeira base")}
              </Button>
            </div>
          ) : (
            <Table stickyHeader className={classes.excelTable}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("table.name", "Nome")}</TableCell>
                  <TableCell>{t("table.type", "Tipo")}</TableCell>
                  <TableCell>Chunks</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>{t("table.file", "Arquivo")}</TableCell>
                  <TableCell>{t("table.createdAt", "Criado em")}</TableCell>
                  <TableCell align="right">{t("table.actions", "Ações")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bases.map((kb) => {
                  const st = statusMap[kb.status] || statusMap.pending;
                  return (
                    <TableRow key={kb.id} className={classes.zebraRow}>
                      <TableCell>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>{kb.name}</Typography>
                        {kb.description && <Typography variant="caption" color="textSecondary">{kb.description}</Typography>}
                      </TableCell>
                      <TableCell><Chip label={kb.sourceType?.toUpperCase()} size="small" variant="outlined" style={{ borderRadius: 6, fontSize: 11 }} /></TableCell>
                      <TableCell>{kb.chunkCount || 0}</TableCell>
                      <TableCell>
                        <Chip label={t(`status.${kb.status || "pending"}`, st.label)} size="small" className={classes.statusChip}
                          style={kb.status === "indexed" ? { backgroundColor: "#2EA85A", color: "#fff" } : {}} />
                      </TableCell>
                      <TableCell>{kb.fileName || "-"}</TableCell>
                      <TableCell>{new Date(kb.createdAt).toLocaleDateString("pt-BR")}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("tooltips.index", "Indexar conteúdo")}>
                          <IconButton className={classes.iconActionBtn} onClick={() => { setSelectedKB(kb); setIngestOpen(true); setIngestMode("file"); }}>
                            <UploadIcon size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("tooltips.delete", "Excluir")}>
                          <IconButton className={classes.deleteActionBtn} onClick={() => handleDelete(kb.id)}>
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </Paper>

      {/* Dialog: Criar Base */}
      <ChatiaDialog
        open={createOpen} 
        onClose={() => setCreateOpen(false)} 
        maxWidth="sm" 
        title={t("dialogs.newBaseTitle", "Nova Base de Conhecimento")}
        onSave={handleCreate}
        saveText={t("actions.createBase", "Criar Base")}
        saveDisabled={!newName.trim()}
        footerChildren={<CancelButton onClick={() => setCreateOpen(false)}>{t("actions.cancel", "Cancelar")}</CancelButton>}
      >
          <TextField 
            label={t("form.name", "Nome")} 
            value={newName} 
            onChange={(e) => setNewName(e.target.value)} 
            variant="outlined" 
            margin="dense" 
            fullWidth 
            required 
            placeholder={t("form.namePlaceholder", "Ex.: Catálogo de Produtos, FAQ, Políticas...")} 
            InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} 
          />
          <TextField 
            label={t("form.description", "Descrição")} 
            value={newDescription} 
            onChange={(e) => setNewDescription(e.target.value)} 
            variant="outlined" 
            margin="dense" 
            fullWidth 
            multiline 
            minRows={2} 
            placeholder={t("form.descriptionPlaceholder", "Descreva o conteúdo desta base...")} 
            InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} 
          />
          <FormControl fullWidth margin="dense" variant="outlined" style={{ marginTop: 16 }}>
            <InputLabel>{t("form.sourceType", "Tipo de Fonte")}</InputLabel>
            <Select 
              className={classes.reportSelect}
              value={newSourceType} 
              onChange={(e) => setNewSourceType(e.target.value)} 
              label={t("form.sourceType", "Tipo de Fonte")} 
              style={{ borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" }}
              MenuProps={menuProps}
            >
              {sourceTypeOptions.map((opt) => (
                <MenuItem 
                  key={opt.value} 
                  value={opt.value} 
                  className={classes.reportSelectMenuItem}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  {opt.icon} {t(`sourceTypes.${opt.value}`, opt.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={6}>
              <TextField label={t("form.chunkSize", "Tamanho do Chunk")} value={newChunkSize} onChange={(e) => setNewChunkSize(parseInt(e.target.value) || 512)} variant="outlined" margin="dense" fullWidth type="number" helperText={t("form.recommended512", "Recomendado: 512")} InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Overlap" value={newChunkOverlap} onChange={(e) => setNewChunkOverlap(parseInt(e.target.value) || 50)} variant="outlined" margin="dense" fullWidth type="number" helperText={t("form.recommended50", "Recomendado: 50")} InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
            </Grid>
          </Grid>
      </ChatiaDialog>

      {/* Dialog: Indexar */}
      <ChatiaDialog
        open={ingestOpen} 
        onClose={() => !ingesting && setIngestOpen(false)} 
        maxWidth="md" 
        title={t("dialogs.indexTitle", "Indexar Conteúdo - {{name}}", { name: selectedKB?.name || "" })}
        onSave={handleIngest}
        saveText={ingesting ? t("actions.processing", "Processando...") : t("actions.indexKnowledge", "Indexar Conhecimento")}
        saveDisabled={ingesting || !ingestApiKey || (ingestMode === "file" && !ingestFile) || (ingestMode === "text" && !ingestContent.trim()) || (ingestMode === "url" && !ingestUrl.trim())}
        saveLoading={ingesting}
        footerChildren={<CancelButton onClick={() => setIngestOpen(false)} disabled={ingesting}>{t("actions.cancel", "Cancelar")}</CancelButton>}
      >
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {t("dialogs.indexDescription", "Escolha como deseja adicionar conteúdo. Ele será dividido em chunks e indexado com embeddings para busca semântica pelo agente de IA.")}
          </Typography>
          <TextField 
            label={t("form.apiKeyLabel", "API Key (OpenAI)")} 
            value={ingestApiKey} 
            onChange={(e) => setIngestApiKey(e.target.value)}
            type={showApiKey ? "text" : "password"} 
            variant="outlined" 
            margin="dense" 
            fullWidth 
            required
            helperText={t("form.apiKeyHelp", "Necessária para gerar embeddings")}
            InputProps={{ 
              style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowApiKey(!showApiKey)} size="small">
                    {showApiKey ? <VisibilityOffIcon size={18} /> : <VisibilityIcon size={18} />}
                  </IconButton>
                </InputAdornment>
              )
            }} 
          />
          <Box mt={2} mb={2} display="flex" style={{ gap: 8 }}>
            <Button variant={ingestMode === "file" ? "contained" : "outlined"} color={ingestMode === "file" ? "primary" : "default"} size="small" onClick={() => setIngestMode("file")} startIcon={<UploadIcon size={16} />} style={{ borderRadius: 10, textTransform: "none", fontWeight: 600, padding: "8px 16px" }}>{t("ingestModes.file", "Arquivo")}</Button>
            <Button variant={ingestMode === "text" ? "contained" : "outlined"} color={ingestMode === "text" ? "primary" : "default"} size="small" onClick={() => setIngestMode("text")} startIcon={<TextIcon size={16} />} style={{ borderRadius: 10, textTransform: "none", fontWeight: 600, padding: "8px 16px" }}>{t("ingestModes.text", "Texto")}</Button>
            <Button variant={ingestMode === "url" ? "contained" : "outlined"} color={ingestMode === "url" ? "primary" : "default"} size="small" onClick={() => setIngestMode("url")} startIcon={<LinkIcon size={16} />} style={{ borderRadius: 10, textTransform: "none", fontWeight: 600, padding: "8px 16px" }}>{t("ingestModes.url", "URL / Site")}</Button>
          </Box>
          {ingestMode === "file" && (
            <Box mt={1}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon size={18} />} fullWidth style={{ justifyContent: "flex-start", padding: "16px", borderRadius: 12, textTransform: "none", backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)", borderStyle: "dashed" }}>
                <span style={{ marginLeft: 8 }}>{ingestFile ? ingestFile.name : t("form.filePlaceholder", "Clique para selecionar arquivo (PDF, TXT, CSV...)")}</span>
                <input type="file" hidden accept=".pdf,.txt,.csv,.md,.docx" onChange={(e) => setIngestFile(e.target.files[0] || null)} />
              </Button>
            </Box>
          )}
          {ingestMode === "text" && (
            <Box mt={1}>
              <TextField label={t("form.content", "Conteúdo")} value={ingestContent} onChange={(e) => setIngestContent(e.target.value)} variant="outlined" margin="dense" fullWidth multiline minRows={6} maxRows={15} placeholder={t("form.contentPlaceholder", "Cole aqui o texto...")} InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
            </Box>
          )}
          {ingestMode === "url" && (
            <Box mt={1}>
              <TextField label={t("form.siteUrl", "URL do site")} value={ingestUrl} onChange={(e) => setIngestUrl(e.target.value)} variant="outlined" margin="dense" fullWidth placeholder="https://..." helperText={t("form.siteUrlHelp", "Insira a URL completa")} InputProps={{ style: { borderRadius: 12, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" } }} />
            </Box>
          )}
          {ingesting && (
            <Box mt={3} display="flex" alignItems="center" justifyContent="center" p={2} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)", borderRadius: 12, border: `1px solid ${hexToRgba(PRIMARY_MAIN, 0.2)}` }}>
              <CircularProgress size={24} style={{ marginRight: 16 }} />
              <Typography variant="body2" style={{ fontWeight: 600 }}>{t("dialogs.processingDescription", "Processando inteligência... Isso pode levar alguns minutos.")}</Typography>
            </Box>
          )}
      </ChatiaDialog>
    </div>
  );
};

export default RAGTab;
